import { supabase } from "@/integrations/supabase/client";
import { inviteApprovedPhotographer } from "@/lib/photographer-invite.functions";
import { inviteApprovedVisagist } from "@/lib/visagist-invite.functions";
import { sendTransactionalEmail } from "@/lib/email/send";

export type ApplicationStatus = "pending" | "approved" | "rejected";

/**
 * Feste Produktions-Domain für E-Mail-Links. Niemals window.location.origin
 * verwenden, da Freigaben aus dem Editor (lovable.dev) sonst Links auf die
 * Plattform statt auf die Website erzeugen.
 */
const SITE_ORIGIN = "https://omselected.art";
export type BriefStatus = "new" | "in_review" | "published" | "closed" | "rejected";

export async function checkIsAdmin(userId: string) {
  const { data, error } = await supabase.rpc("has_role", {
    _user_id: userId,
    _role: "admin",
  });
  if (error) return false;
  return !!data;
}

export type BackofficeRole = "admin" | "curator" | null;

/** Liefert die Backoffice-Rolle: 'admin', 'curator' oder null (kein Zugang). */
export async function getBackofficeRole(userId: string): Promise<BackofficeRole> {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  if (error || !data) return null;
  const roles = data.map((r) => r.role as string);
  if (roles.includes("admin")) return "admin";
  if (roles.includes("curator")) return "curator";
  return null;
}

export async function getCounts() {
  const [pendingPhotoApps, pendingVisaApps, newBriefs, approvedPhoto, approvedVisa, pendingCommunity] = await Promise.all([
    supabase.from("photographer_applications").select("*", { count: "exact", head: true }).eq("status", "pending"),
    supabase.from("visagist_applications").select("*", { count: "exact", head: true }).eq("status", "pending"),
    supabase.from("client_briefs").select("*", { count: "exact", head: true }).eq("status", "new"),
    supabase.from("photographers").select("*", { count: "exact", head: true }).eq("published", true),
    supabase.from("visagists").select("*", { count: "exact", head: true }).eq("published", true),
    supabase.from("community_submissions").select("*", { count: "exact", head: true }).eq("status", "pending"),
  ]);
  return {
    pendingPhotoApplications: pendingPhotoApps.count ?? 0,
    pendingVisagistApplications: pendingVisaApps.count ?? 0,
    newBriefs: newBriefs.count ?? 0,
    approvedPhotographers: approvedPhoto.count ?? 0,
    approvedVisagists: approvedVisa.count ?? 0,
    pendingCommunity: pendingCommunity.count ?? 0,
  };
}

export async function listApplications(status: ApplicationStatus | "all") {
  let q = supabase.from("photographer_applications").select("*").order("created_at", { ascending: false });
  if (status !== "all") q = q.eq("status", status);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export async function listBriefs(status: BriefStatus | "all") {
  let q = supabase.from("client_briefs").select("*").order("created_at", { ascending: false });
  if (status !== "all") q = q.eq("status", status);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export async function updateApplication(id: string, status: ApplicationStatus, admin_note: string | null) {
  const { data: { user } } = await supabase.auth.getUser();
  const { data: prev } = await supabase
    .from("photographer_applications")
    .select("status, email, full_name")
    .eq("id", id)
    .maybeSingle();
  const { error } = await supabase
    .from("photographer_applications")
    .update({
      status,
      admin_note,
      reviewed_at: new Date().toISOString(),
      reviewed_by: user?.id,
    })
    .eq("id", id);
  if (error) throw error;

  // On approve: publish a public photographer profile from the application.
  if (status === "approved") {
    await publishPhotographerFromApplication(id);
  }

  if (status === "rejected" && prev && prev.status !== "rejected" && prev.email) {
    try {
      await sendTransactionalEmail({
        templateName: "rejection-notice",
        recipientEmail: prev.email,
        idempotencyKey: `photographer-rejected-${id}`,
        templateData: { name: prev.full_name ?? undefined, message: admin_note ?? null },
      });
    } catch (e) {
      console.error("[photographer-rejected email]", e);
    }
  }
}

function slugify(input: string) {
  return input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "")
    .slice(0, 60) || "photographer";
}

async function publishPhotographerFromApplication(applicationId: string) {
  // Skip if a profile for this application already exists.
  const { data: existing } = await supabase
    .from("photographers")
    .select("id")
    .eq("application_id", applicationId)
    .maybeSingle();
  if (existing) return;

  const { data: app, error: appErr } = await supabase
    .from("photographer_applications")
    .select("*")
    .eq("id", applicationId)
    .single();
  if (appErr || !app) return;

  // Find a free slug.
  const base = slugify(app.full_name);
  let slug = base;
  for (let i = 2; i < 50; i++) {
    const { data: clash } = await supabase
      .from("photographers")
      .select("id")
      .eq("slug", slug)
      .maybeSingle();
    if (!clash) break;
    slug = `${base}-${i}`;
  }

  const samples = (app.sample_image_urls ?? []);
  const portrait = (app as { portrait_url?: string | null }).portrait_url ?? samples[0] ?? null;
  const gallery = samples;
  const galleryThemes = ((app as { sample_image_themes?: string[] | null }).sample_image_themes ?? []).slice(0, gallery.length);

  // Try to link to an existing auth user by email (admin-only RPC).
  let linkedUserId: string | null = null;
  if (app.email) {
    try {
      const res = await inviteApprovedPhotographer({
        data: {
          email: app.email,
          fullName: app.full_name,
          redirectTo: `${SITE_ORIGIN}/reset-password`,
        },
      });
      linkedUserId = res.userId;
    } catch (e) {
      // Invite-Fehler darf das Freischalten nicht blockieren.
      console.error("[invite photographer]", e);
    }
  }

  const { error: insErr } = await supabase.from("photographers").insert({
    application_id: app.id,
    slug,
    name: app.full_name,
    region: app.region ?? app.city ?? null,
    themes: app.themes ?? [],
    bio: app.bio,
    email: app.email,
    portrait_url: portrait,
    gallery_urls: gallery,
    gallery_themes: galleryThemes,
    published: true,
    country: app.country ?? null,
    city: app.city ?? null,
    price_from: app.price_from ?? null,
    tfp: app.tfp ?? false,
    price_on_request: app.price_on_request ?? false,
    website_url: app.website_url ?? null,
    instagram_handle: app.instagram_handle ?? null,
    user_id: linkedUserId,
  });
  if (insErr) throw insErr;
}

export async function updateBrief(id: string, status: BriefStatus, admin_note: string | null) {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase
    .from("client_briefs")
    .update({
      status,
      admin_note,
      reviewed_at: new Date().toISOString(),
      reviewed_by: user?.id,
    })
    .eq("id", id);
  if (error) throw error;
}

// ----- Photo of the day -----

export type PotdRow = {
  date: string;
  photographer_id: string | null;
  photographer_slug: string | null;
  photographer_name: string | null;
  region: string | null;
  image_url: string;
  created_at: string;
};

export type PhotographerOption = {
  id: string;
  slug: string;
  name: string;
  region: string | null;
  gallery_urls: string[];
  portrait_url: string | null;
};

function todayUtc() {
  return new Date().toISOString().slice(0, 10);
}

export async function listPotd(limit = 60): Promise<PotdRow[]> {
  const { data, error } = await supabase
    .from("photo_of_the_day")
    .select("*")
    .order("date", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as PotdRow[];
}

export async function listPublishedPhotographers(): Promise<PhotographerOption[]> {
  const { data, error } = await supabase
    .from("photographers")
    .select("id, slug, name, region, gallery_urls, portrait_url")
    .eq("published", true)
    .order("name", { ascending: true });
  if (error) throw error;
  return (data ?? []) as PhotographerOption[];
}

export async function regenerateTodayPotd() {
  const today = todayUtc();
  const del = await supabase.from("photo_of_the_day").delete().eq("date", today);
  if (del.error) throw del.error;
  const { error } = await supabase.rpc("pick_photo_of_the_day");
  if (error) throw error;
}

export async function setPotdForDate(date: string, photographer: PhotographerOption, image_url: string) {
  const { error } = await supabase
    .from("photo_of_the_day")
    .upsert({
      date,
      photographer_id: photographer.id,
      photographer_slug: photographer.slug,
      photographer_name: photographer.name,
      region: photographer.region,
      image_url,
    }, { onConflict: "date" });
  if (error) throw error;
}

export async function deletePotd(date: string) {
  const { error } = await supabase.from("photo_of_the_day").delete().eq("date", date);
  if (error) throw error;
}

// ----- Photographers (admin) -----

export type AdminPhotographer = {
  id: string;
  slug: string;
  name: string;
  email: string | null;
  region: string | null;
  country: string | null;
  city: string | null;
  published: boolean;
  founding_member: boolean;
  user_id: string | null;
  application_id: string | null;
  created_at: string;
  max_gallery_images: number | null;
};

export async function listAllPhotographers(): Promise<AdminPhotographer[]> {
  const { data, error } = await supabase
    .from("photographers")
    .select("id, slug, name, email, region, country, city, published, founding_member, user_id, application_id, created_at, max_gallery_images")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as AdminPhotographer[];
}

export async function deletePhotographer(id: string) {
  // Remove any photo-of-the-day entries that reference this photographer first
  // to avoid stale references (FK is not enforced but we keep data clean).
  await supabase.from("photo_of_the_day").delete().eq("photographer_id", id);
  const { error } = await supabase.from("photographers").delete().eq("id", id);
  if (error) throw error;
}

export async function setPhotographerPublished(id: string, published: boolean) {
  const { error } = await supabase.from("photographers").update({ published }).eq("id", id);
  if (error) throw error;
}

export async function setPhotographerFoundingMember(id: string, founding_member: boolean) {
  const { error } = await supabase.from("photographers").update({ founding_member }).eq("id", id);
  if (error) throw error;
}

export async function setPhotographerMaxGallery(id: string, max_gallery_images: number | null) {
  const { error } = await supabase.from("photographers").update({ max_gallery_images }).eq("id", id);
  if (error) throw error;
}

// ----- Brief replies -----

export type BriefReply = {
  id: string;
  brief_id: string;
  recipient_email: string;
  body: string;
  photographer_ids: string[];
  sent_at: string;
  created_at: string;
};

export async function listBriefReplies(briefId: string): Promise<BriefReply[]> {
  const { data, error } = await supabase
    .from("brief_replies")
    .select("*")
    .eq("brief_id", briefId)
    .order("sent_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as BriefReply[];
}

export async function createBriefReply(input: {
  brief_id: string;
  recipient_email: string;
  body: string;
  photographer_ids: string[];
}): Promise<BriefReply> {
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("brief_replies")
    .insert({ ...input, created_by: user?.id })
    .select("*")
    .single();
  if (error) throw error;
  return data as BriefReply;
}

export type PhotographerPick = {
  id: string;
  slug: string;
  name: string;
  region: string | null;
  portrait_url: string | null;
};

export async function searchPhotographersForReply(query: string): Promise<PhotographerPick[]> {
  let q = supabase
    .from("photographers")
    .select("id, slug, name, region, portrait_url")
    .eq("published", true)
    .order("name", { ascending: true })
    .limit(20);
  const term = query.trim();
  if (term.length > 0) {
    q = q.or(`name.ilike.%${term}%,region.ilike.%${term}%,city.ilike.%${term}%`);
  }
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as PhotographerPick[];
}

export async function getPhotographersByIds(ids: string[]): Promise<PhotographerPick[]> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from("photographers")
    .select("id, slug, name, region, portrait_url")
    .in("id", ids);
  if (error) throw error;
  return (data ?? []) as PhotographerPick[];
}

// ----- Visagist applications & profiles (admin) -----

export async function listVisagistApplications(status: ApplicationStatus | "all") {
  let q = supabase.from("visagist_applications").select("*").order("created_at", { ascending: false });
  if (status !== "all") q = q.eq("status", status);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export async function updateVisagistApplication(id: string, status: ApplicationStatus, admin_note: string | null) {
  const { data: { user } } = await supabase.auth.getUser();
  const { data: prev } = await supabase
    .from("visagist_applications")
    .select("status, email, full_name")
    .eq("id", id)
    .maybeSingle();
  const { error } = await supabase
    .from("visagist_applications")
    .update({
      status,
      admin_note,
      reviewed_at: new Date().toISOString(),
      reviewed_by: user?.id,
    })
    .eq("id", id);
  if (error) throw error;

  if (status === "approved") {
    await publishVisagistFromApplication(id);
  }

  if (status === "rejected" && prev && prev.status !== "rejected" && prev.email) {
    try {
      await sendTransactionalEmail({
        templateName: "rejection-notice",
        recipientEmail: prev.email,
        idempotencyKey: `visagist-rejected-${id}`,
        templateData: { name: prev.full_name ?? undefined, message: admin_note ?? null },
      });
    } catch (e) {
      console.error("[visagist-rejected email]", e);
    }
  }
}

async function publishVisagistFromApplication(applicationId: string) {
  const { data: existing } = await supabase
    .from("visagists")
    .select("id")
    .eq("application_id", applicationId)
    .maybeSingle();
  if (existing) return;

  const { data: app, error: appErr } = await supabase
    .from("visagist_applications")
    .select("*")
    .eq("id", applicationId)
    .single();
  if (appErr || !app) return;

  const base = slugify(app.full_name);
  let slug = base;
  for (let i = 2; i < 50; i++) {
    const { data: clash } = await supabase
      .from("visagists")
      .select("id")
      .eq("slug", slug)
      .maybeSingle();
    if (!clash) break;
    slug = `${base}-${i}`;
  }

  const samples = (app.sample_image_urls ?? []);
  const portrait = (app as { portrait_url?: string | null }).portrait_url ?? samples[0] ?? null;
  const gallery = samples;
  const credits = ((app as { sample_image_credits?: string[] | null }).sample_image_credits ?? []).slice(0, gallery.length);
  const galleryThemes = ((app as { sample_image_themes?: string[] | null }).sample_image_themes ?? []).slice(0, gallery.length);

  let linkedUserId: string | null = null;
  if (app.email) {
    try {
      const res = await inviteApprovedVisagist({
        data: {
          email: app.email,
          fullName: app.full_name,
          redirectTo: `${SITE_ORIGIN}/reset-password`,
        },
      });
      linkedUserId = res.userId;
    } catch (e) {
      console.error("[invite visagist]", e);
    }
  }

  const { error: insErr } = await supabase.from("visagists").insert({
    application_id: app.id,
    slug,
    name: app.full_name,
    region: app.region ?? app.city ?? null,
    themes: app.themes ?? [],
    bio: app.bio,
    email: app.email,
    portrait_url: portrait,
    gallery_urls: gallery,
    gallery_credits: credits,
    gallery_themes: galleryThemes,
    published: true,
    country: app.country ?? null,
    city: app.city ?? null,
    price_from: app.price_from ?? null,
    tfp: app.tfp ?? false,
    price_on_request: app.price_on_request ?? false,
    website_url: app.website_url ?? null,
    instagram_handle: app.instagram_handle ?? null,
    user_id: linkedUserId,
  });
  if (insErr) throw insErr;
}

export type AdminVisagist = {
  id: string;
  slug: string;
  name: string;
  email: string | null;
  region: string | null;
  country: string | null;
  city: string | null;
  published: boolean;
  founding_member: boolean;
  user_id: string | null;
  application_id: string | null;
  created_at: string;
  max_gallery_images: number | null;
};

export async function listAllVisagists(): Promise<AdminVisagist[]> {
  const { data, error } = await supabase
    .from("visagists")
    .select("id, slug, name, email, region, country, city, published, founding_member, user_id, application_id, created_at, max_gallery_images")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as AdminVisagist[];
}

export async function deleteVisagist(id: string) {
  const { error } = await supabase.from("visagists").delete().eq("id", id);
  if (error) throw error;
}

export async function setVisagistPublished(id: string, published: boolean) {
  const { error } = await supabase.from("visagists").update({ published }).eq("id", id);
  if (error) throw error;
}

export async function setVisagistFoundingMember(id: string, founding_member: boolean) {
  const { error } = await supabase.from("visagists").update({ founding_member }).eq("id", id);
  if (error) throw error;
}

export async function setVisagistMaxGallery(id: string, max_gallery_images: number | null) {
  const { error } = await supabase.from("visagists").update({ max_gallery_images }).eq("id", id);
  if (error) throw error;
}

// ----- Notification settings -----

export const NOTIFY_DEFAULT_EMAIL = "kontakt@om-selected.art";

export type NotificationSettings = {
  recipientEmails: string[];
  notifyOnApplication: boolean;
  notifyOnSelected: boolean;
};

export async function getNotificationSettings(): Promise<NotificationSettings> {
  const { data, error } = await supabase
    .from("app_settings")
    .select("key, value_text, value_int")
    .in("key", ["notify_recipient_email", "notify_on_application", "notify_on_selected"]);
  if (error) throw error;
  const byKey = new Map((data ?? []).map((r) => [r.key, r]));
  const email = byKey.get("notify_recipient_email")?.value_text;
  const app = byKey.get("notify_on_application")?.value_int;
  const sel = byKey.get("notify_on_selected")?.value_int;
  const emails = (email ?? "")
    .split(",")
    .map((e) => e.trim())
    .filter((e) => e.length > 0);
  return {
    recipientEmails: emails.length > 0 ? emails : [NOTIFY_DEFAULT_EMAIL],
    notifyOnApplication: app == null ? true : app === 1,
    notifyOnSelected: sel == null ? true : sel === 1,
  };
}

export async function saveNotificationSettings(settings: NotificationSettings) {
  const { data: { user } } = await supabase.auth.getUser();
  const emailsText = settings.recipientEmails.map((e) => e.trim()).filter((e) => e.length > 0).join(",");
  const rows = [
    { key: "notify_recipient_email", value_text: emailsText, value_int: null, updated_by: user?.id, updated_at: new Date().toISOString() },
    { key: "notify_on_application", value_int: settings.notifyOnApplication ? 1 : 0, value_text: null, updated_by: user?.id, updated_at: new Date().toISOString() },
    { key: "notify_on_selected", value_int: settings.notifyOnSelected ? 1 : 0, value_text: null, updated_by: user?.id, updated_at: new Date().toISOString() },
  ];
  const { error } = await supabase.from("app_settings").upsert(rows, { onConflict: "key" });
  if (error) throw error;
}